import { useState } from 'react'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../../components/ui/dialog'
import { Button } from '../../../components/ui/button'
import { useInviteUser } from '../../../api/routes/users'
import { useGetAllBusinesses } from '../../../api/routes/businesses'
import { toast } from 'react-toastify'

interface InviteUserDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
}

const initialForm = {
    firstName: '',
    lastName: '',
    email: '',
    role: 'user',
    businessId: '',
}

export function InviteUserDialog({
    open,
    onOpenChange,
}: InviteUserDialogProps) {
    const [form, setForm] = useState(initialForm)
    const { data: businesses } = useGetAllBusinesses()
    const inviteUser = useInviteUser()

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()

        if (!form.firstName || !form.lastName || !form.email) {
            toast.error('First name, last name, and email are required')
            return
        }

        inviteUser.mutate(
            {
                firstName: form.firstName,
                lastName: form.lastName,
                email: form.email,
                role: form.role,
                businessId: form.businessId || null,
            },
            {
                onSuccess: () => {
                    toast.success('User invited successfully')
                    setForm(initialForm)
                    onOpenChange(false)
                },
                onError: (error: any) => {
                    toast.error(
                        error?.response?.data?.message ||
                            error.message ||
                            'Failed to invite user'
                    )
                },
            }
        )
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Invite User</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="flex gap-3">
                        <div className="flex-1 space-y-1.5">
                            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                First Name
                            </label>
                            <input
                                value={form.firstName}
                                onChange={(e) =>
                                    setForm((f) => ({
                                        ...f,
                                        firstName: e.target.value,
                                    }))
                                }
                                required
                                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                            />
                        </div>
                        <div className="flex-1 space-y-1.5">
                            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Last Name
                            </label>
                            <input
                                value={form.lastName}
                                onChange={(e) =>
                                    setForm((f) => ({
                                        ...f,
                                        lastName: e.target.value,
                                    }))
                                }
                                required
                                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                            />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            Email
                        </label>
                        <input
                            type="email"
                            value={form.email}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    email: e.target.value,
                                }))
                            }
                            required
                            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            Role
                        </label>
                        <select
                            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                            value={form.role}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    role: e.target.value,
                                }))
                            }
                        >
                            <option value="user">User</option>
                            <option value="businessAdmin">
                                Business Admin
                            </option>
                            <option value="systemAdmin">
                                System Admin
                            </option>
                        </select>
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            Business
                        </label>
                        <select
                            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                            value={form.businessId}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    businessId: e.target.value,
                                }))
                            }
                        >
                            <option value="">None (System-level)</option>
                            {businesses?.map((business: any) => (
                                <option
                                    key={business.id}
                                    value={business.id}
                                >
                                    {business.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <DialogFooter className="flex justify-end gap-2 pt-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={inviteUser.isPending}
                        >
                            {inviteUser.isPending
                                ? 'Sending...'
                                : 'Send Invite'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}
